import { useState, useMemo, useCallback, useRef } from 'react'
import { Search, Plus, Bookmark, LayoutGrid, List, Clock } from 'lucide-react'
import { CARD_TYPES, CARD_CATEGORIES } from '@/lib/cms/card-registry'
import type { CanvasBlock } from '@/lib/cms/card-registry'
import type { BlockTemplate } from '@/pages/admin/cms-editor/types'

/* ── Props ───────────────────────────────────────────── */

interface ComponentDrawerProps {
  blocks: CanvasBlock[]
  templates: BlockTemplate[]
  onAddBlock: (type: string, insertIndex?: number) => void
  onAddPreset: (blocks: CanvasBlock[]) => void
  onSaveTemplate: (blocks: CanvasBlock[], name: string) => void
}

/* ── Category label mapping for IDE ──────────────────── */

const IDE_CATEGORY_LABELS: Record<string, string> = {
  content: 'CONTENT',
  data: 'DATA-BOUND',
  media: 'MEDIA',
  layout: 'LAYOUT',
}

/* ── Thumbnail art for each card type ────────────────── */

const CARD_THUMBNAILS: Record<string, React.ReactNode> = {
  'hero-banner': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="8" y="8" width="20" height="3" rx="1.5" fill="#3b82f6" opacity=".6" />
      <rect x="8" y="14" width="32" height="2" rx="1" fill="#fff" opacity=".12" />
      <rect x="8" y="19" width="12" height="4" rx="2" fill="#3b82f6" opacity=".3" />
    </svg>
  ),
  'text-block': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="6" y="6" width="36" height="2" rx="1" fill="#fff" opacity=".15" />
      <rect x="6" y="11" width="30" height="2" rx="1" fill="#fff" opacity=".1" />
      <rect x="6" y="16" width="34" height="2" rx="1" fill="#fff" opacity=".1" />
      <rect x="6" y="21" width="18" height="2" rx="1" fill="#fff" opacity=".08" />
    </svg>
  ),
  'cta-block': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="10" y="8" width="28" height="3" rx="1.5" fill="#fff" opacity=".15" />
      <rect x="14" y="16" width="20" height="6" rx="3" fill="#3b82f6" opacity=".4" />
    </svg>
  ),
  'heading-block': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="6" y="10" width="24" height="4" rx="2" fill="#fff" opacity=".2" />
      <rect x="6" y="18" width="36" height="2" rx="1" fill="#fff" opacity=".08" />
    </svg>
  ),
  'service-card': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="4" y="3" width="18" height="10" rx="2" fill="#3b82f6" opacity=".15" />
      <rect x="4" y="16" width="14" height="2" rx="1" fill="#fff" opacity=".15" />
      <rect x="4" y="21" width="10" height="2" rx="1" fill="#fff" opacity=".08" />
      <rect x="26" y="3" width="18" height="10" rx="2" fill="#8b5cf6" opacity=".12" />
      <rect x="26" y="16" width="14" height="2" rx="1" fill="#fff" opacity=".15" />
      <rect x="26" y="21" width="10" height="2" rx="1" fill="#fff" opacity=".08" />
    </svg>
  ),
  'testimonial-card': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <text x="6" y="12" fontSize="10" fill="#3b82f6" opacity=".3">
        ❝
      </text>
      <rect x="14" y="8" width="28" height="2" rx="1" fill="#fff" opacity=".1" />
      <rect x="14" y="13" width="22" height="2" rx="1" fill="#fff" opacity=".08" />
      <circle cx="18" cy="22" r="3" fill="#fff" opacity=".1" />
      <rect x="24" y="21" width="12" height="2" rx="1" fill="#fff" opacity=".08" />
    </svg>
  ),
  'team-card': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <circle cx="24" cy="10" r="5" fill="#8b5cf6" opacity=".2" />
      <rect x="14" y="18" width="20" height="2" rx="1" fill="#fff" opacity=".15" />
      <rect x="18" y="23" width="12" height="2" rx="1" fill="#fff" opacity=".08" />
    </svg>
  ),
  'image-card': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="4" y="3" width="40" height="18" rx="2" fill="#3b82f6" opacity=".08" />
      <circle cx="14" cy="10" r="3" fill="#fbbf24" opacity=".2" />
      <path
        d="M4 18 L18 10 L28 16 L36 12 L44 18"
        stroke="#3b82f6"
        opacity=".15"
        strokeWidth="1.5"
        fill="none"
      />
      <rect x="4" y="23" width="20" height="2" rx="1" fill="#fff" opacity=".08" />
    </svg>
  ),
  'gallery-grid': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="3" y="3" width="13" height="10" rx="2" fill="#3b82f6" opacity=".12" />
      <rect x="18" y="3" width="13" height="10" rx="2" fill="#8b5cf6" opacity=".12" />
      <rect x="33" y="3" width="12" height="10" rx="2" fill="#ec4899" opacity=".12" />
      <rect x="3" y="15" width="13" height="10" rx="2" fill="#10b981" opacity=".12" />
      <rect x="18" y="15" width="13" height="10" rx="2" fill="#f59e0b" opacity=".12" />
      <rect x="33" y="15" width="12" height="10" rx="2" fill="#3b82f6" opacity=".12" />
    </svg>
  ),
  divider: (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <line
        x1="8"
        y1="14"
        x2="40"
        y2="14"
        stroke="#fff"
        opacity=".12"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
    </svg>
  ),
  spacer: (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <line
        x1="24"
        y1="6"
        x2="24"
        y2="22"
        stroke="#fff"
        opacity=".08"
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <path d="M22 8 L24 5 L26 8" stroke="#fff" opacity=".12" strokeWidth="1" fill="none" />
      <path d="M22 20 L24 23 L26 20" stroke="#fff" opacity=".12" strokeWidth="1" fill="none" />
    </svg>
  ),
  'event-card': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="6" y="4" width="10" height="10" rx="2" fill="#ec4899" opacity=".15" />
      <rect x="8" y="6" width="6" height="2" rx="1" fill="#fff" opacity=".2" />
      <rect x="8" y="10" width="4" height="2" rx="1" fill="#fff" opacity=".1" />
      <rect x="20" y="6" width="22" height="2" rx="1" fill="#fff" opacity=".15" />
      <rect x="20" y="11" width="16" height="2" rx="1" fill="#fff" opacity=".08" />
      <rect x="6" y="18" width="36" height="6" rx="3" fill="#ec4899" opacity=".12" />
    </svg>
  ),
  'stylist-flip-card': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="4" y="2" width="18" height="24" rx="3" fill="#8b5cf6" opacity=".1" />
      <circle cx="13" cy="10" r="4" fill="#8b5cf6" opacity=".2" />
      <rect x="6" y="17" width="14" height="2" rx="1" fill="#fff" opacity=".12" />
      <rect x="8" y="22" width="10" height="2" rx="1" fill="#fff" opacity=".06" />
      <rect x="26" y="2" width="18" height="24" rx="3" fill="#3b82f6" opacity=".1" />
      <circle cx="35" cy="10" r="4" fill="#3b82f6" opacity=".2" />
      <rect x="28" y="17" width="14" height="2" rx="1" fill="#fff" opacity=".12" />
      <rect x="30" y="22" width="10" height="2" rx="1" fill="#fff" opacity=".06" />
    </svg>
  ),
  'looks-gallery': (
    <svg viewBox="0 0 48 28" fill="none">
      <rect width="48" height="28" rx="3" fill="#1a1a2e" />
      <rect x="3" y="3" width="13" height="16" rx="2" fill="#f59e0b" opacity=".12" />
      <rect x="18" y="3" width="13" height="16" rx="2" fill="#ec4899" opacity=".12" />
      <rect x="33" y="3" width="12" height="16" rx="2" fill="#8b5cf6" opacity=".12" />
      <rect x="3" y="22" width="8" height="2" rx="1" fill="#fff" opacity=".1" />
      <rect x="18" y="22" width="8" height="2" rx="1" fill="#fff" opacity=".1" />
      <rect x="33" y="22" width="8" height="2" rx="1" fill="#fff" opacity=".1" />
    </svg>
  ),
}

/* ── Recently Used persistence ───────────────────────── */

const RECENT_STORAGE_KEY = 'cms_recent_blocks'
const MAX_RECENT = 5

function getRecentBlocks(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function pushRecentBlock(type: string) {
  const recent = getRecentBlocks().filter((t) => t !== type)
  recent.unshift(type)
  localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

/* ── Component ───────────────────────────────────────── */

export default function ComponentDrawer({
  blocks,
  templates,
  onAddBlock,
  onAddPreset,
  onSaveTemplate,
}: ComponentDrawerProps) {
  const [search, setSearch] = useState('')
  const [showTemplateInput, setShowTemplateInput] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [recentTypes, setRecentTypes] = useState<string[]>(getRecentBlocks)
  const dragImgRef = useRef<HTMLDivElement>(null)

  // Wrap onAddBlock to also track recent usage
  const handleAddBlock = useCallback(
    (type: string, insertIndex?: number) => {
      pushRecentBlock(type)
      setRecentTypes(getRecentBlocks())
      onAddBlock(type, insertIndex)
    },
    [onAddBlock]
  )

  // Filter cards by search
  const filteredCards = useMemo(() => {
    if (!search.trim()) return CARD_TYPES
    const q = search.toLowerCase()
    return CARD_TYPES.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q)
    )
  }, [search])

  // Group by category
  const grouped = useMemo(() => {
    const groups: Record<string, typeof CARD_TYPES> = {}
    filteredCards.forEach((card) => {
      if (!groups[card.category]) groups[card.category] = []
      groups[card.category].push(card)
    })
    return groups
  }, [filteredCards])

  // Recent blocks resolved to card defs
  const recentCards = useMemo(
    () =>
      recentTypes
        .map((type) => CARD_TYPES.find((c) => c.type === type))
        .filter(Boolean) as typeof CARD_TYPES,
    [recentTypes]
  )

  const handleSaveTemplate = () => {
    if (templateName.trim() && blocks.length > 0) {
      onSaveTemplate(blocks, templateName.trim())
      setTemplateName('')
      setShowTemplateInput(false)
    }
  }

  // Custom drag image
  const handleDragStart = useCallback((e: React.DragEvent, card: (typeof CARD_TYPES)[0]) => {
    e.dataTransfer.setData('application/x-card', JSON.stringify({ type: card.type }))
    e.dataTransfer.effectAllowed = 'copy'

    // Create custom drag ghost
    const ghost = dragImgRef.current
    if (ghost) {
      ghost.textContent = `${card.icon} ${card.label}`
      ghost.style.display = 'flex'
      e.dataTransfer.setDragImage(ghost, 20, 16)
      requestAnimationFrame(() => {
        ghost.style.display = 'none'
      })
    }
  }, [])

  // ── Render a single block item ────────────────────
  const renderBlockItem = useCallback(
    (card: (typeof CARD_TYPES)[0], isRecent = false) => {
      const thumb = CARD_THUMBNAILS[card.type]

      if (viewMode === 'grid') {
        return (
          <button
            key={`${isRecent ? 'recent-' : ''}${card.type}`}
            className="editor-tree-row"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              border: 'none',
              textAlign: 'center',
              background: 'transparent',
              padding: '8px 4px',
              gap: 6,
              borderRadius: 8,
              cursor: 'grab',
            }}
            onClick={() => handleAddBlock(card.type)}
            draggable
            onDragStart={(e) => handleDragStart(e, card)}
            data-testid={`component-${card.type}`}
          >
            {thumb && (
              <div
                style={{
                  width: 56,
                  height: 34,
                  flexShrink: 0,
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                {thumb}
              </div>
            )}
            <span
              style={{
                fontSize: 9,
                fontFamily: 'var(--editor-font-mono)',
                color: 'var(--editor-text-dim)',
                lineHeight: 1.2,
              }}
            >
              {card.label}
            </span>
          </button>
        )
      }

      return (
        <button
          key={`${isRecent ? 'recent-' : ''}${card.type}`}
          className="editor-tree-row"
          style={{
            width: '100%',
            border: 'none',
            textAlign: 'left',
            background: 'transparent',
            minHeight: 36,
            cursor: 'grab',
          }}
          onClick={() => handleAddBlock(card.type)}
          draggable
          onDragStart={(e) => handleDragStart(e, card)}
          data-testid={`component-${card.type}`}
        >
          {/* Thumbnail */}
          {thumb ? (
            <div
              style={{
                width: 32,
                height: 20,
                flexShrink: 0,
                borderRadius: 3,
                overflow: 'hidden',
                marginRight: 4,
              }}
            >
              {thumb}
            </div>
          ) : (
            <span
              style={{
                width: 20,
                height: 20,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                flexShrink: 0,
                color: 'var(--editor-text-dim)',
              }}
            >
              {card.icon}
            </span>
          )}
          <span style={{ flex: 1, fontSize: 'var(--editor-font-size)' }}>{card.label}</span>
          {card.shortcut && (
            <span
              style={{
                fontSize: 9,
                fontFamily: 'var(--editor-font-mono)',
                fontWeight: 600,
                color: 'var(--editor-text-dim)',
                background: 'var(--editor-panel)',
                border: '1px solid var(--editor-border-dim)',
                borderRadius: 3,
                padding: '1px 5px',
                lineHeight: '14px',
                flexShrink: 0,
              }}
            >
              {card.shortcut}
            </span>
          )}
          {card.entityType && (
            <span className="editor-badge" style={{ fontSize: 8 }}>
              entity
            </span>
          )}
        </button>
      )
    },
    [viewMode, handleAddBlock, handleDragStart]
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Hidden drag ghost element */}
      <div
        ref={dragImgRef}
        style={{
          position: 'fixed',
          top: -100,
          left: -100,
          display: 'none',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          borderRadius: 8,
          background: '#111118',
          border: '1px solid rgba(59,130,246,0.3)',
          color: '#fff',
          fontSize: 11,
          fontWeight: 600,
          boxShadow: '0 8px 24px rgba(0,0,0,0.6), 0 0 0 1px rgba(59,130,246,0.15)',
          whiteSpace: 'nowrap',
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      />

      {/* Search + view toggle */}
      <div
        style={{
          padding: 'var(--editor-panel-pad)',
          display: 'flex',
          gap: 6,
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, position: 'relative' }}>
          <Search
            size={12}
            style={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--editor-text-dim)',
            }}
          />
          <input
            className="editor-input"
            style={{ paddingLeft: 28 }}
            placeholder="Search blocks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          className={`editor-btn editor-btn-sm ${viewMode === 'grid' ? 'editor-btn-primary' : 'editor-btn-ghost'}`}
          onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          title={viewMode === 'grid' ? 'List view' : 'Grid view'}
          style={{ padding: '4px 6px', flexShrink: 0 }}
        >
          {viewMode === 'grid' ? <List size={12} /> : <LayoutGrid size={12} />}
        </button>
      </div>

      {/* Block list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* ── Recently Used ── */}
        {!search && recentCards.length > 0 && (
          <div>
            <div className="editor-section-header">
              <Clock size={11} style={{ color: 'var(--editor-accent)', flexShrink: 0 }} />
              <span>RECENTLY USED</span>
              <span className="editor-badge">{recentCards.length}</span>
            </div>
            <div
              style={
                viewMode === 'grid'
                  ? {
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 2,
                      padding: '0 4px',
                    }
                  : undefined
              }
            >
              {recentCards.map((card) => renderBlockItem(card, true))}
            </div>
          </div>
        )}

        {/* Card categories */}
        {CARD_CATEGORIES.map((cat) => {
          const cards = grouped[cat.key]
          if (!cards?.length) return null

          return (
            <div key={cat.key}>
              <div className="editor-section-header">
                <span>{IDE_CATEGORY_LABELS[cat.key] || cat.label.toUpperCase()}</span>
                <span className="editor-badge">{cards.length}</span>
              </div>

              <div
                style={
                  viewMode === 'grid'
                    ? {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 2,
                        padding: '0 4px',
                      }
                    : undefined
                }
              >
                {cards.map((card) => renderBlockItem(card))}
              </div>
            </div>
          )
        })}

        {/* Templates section */}
        <div>
          <div className="editor-section-header">
            <Bookmark size={12} />
            <span style={{ flex: 1 }}>MY TEMPLATES</span>
            <span className="editor-badge">{templates.length}</span>
          </div>

          {templates.length === 0 && (
            <div
              style={{
                padding: '12px var(--editor-panel-pad)',
                color: 'var(--editor-text-dim)',
                fontSize: 'var(--editor-font-size-xs)',
              }}
            >
              Save block layouts as reusable templates
            </div>
          )}

          {templates.map((tmpl) => (
            <button
              key={tmpl.id}
              className="editor-tree-row"
              style={{
                width: '100%',
                border: 'none',
                textAlign: 'left',
                background: 'transparent',
              }}
              onClick={() => onAddPreset(tmpl.blocks)}
            >
              <Bookmark size={12} style={{ color: 'var(--editor-accent)', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 'var(--editor-font-size)' }}>{tmpl.name}</span>
              <span className="editor-badge">{tmpl.blocks.length} blocks</span>
            </button>
          ))}

          {/* Save current as template */}
          {showTemplateInput ? (
            <div style={{ padding: '4px var(--editor-panel-pad)', display: 'flex', gap: 4 }}>
              <input
                className="editor-input editor-input-sm"
                placeholder="Template name…"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTemplate()
                  if (e.key === 'Escape') setShowTemplateInput(false)
                }}
              />
              <button
                className="editor-btn editor-btn-primary editor-btn-sm"
                onClick={handleSaveTemplate}
              >
                Save
              </button>
            </div>
          ) : (
            <button
              className="editor-btn editor-btn-ghost editor-btn-sm"
              style={{ width: '100%', justifyContent: 'center', margin: '4px 0' }}
              onClick={() => setShowTemplateInput(true)}
              disabled={blocks.length === 0}
            >
              <Plus size={10} /> Save Current as Template
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
